import { toPrivateEventId } from '@/utils/picoEventId';
import { EventInteractions, type EventInteractionWithContext } from './types';

/**
 * Marks every PicoX payload built by this module as originating in the
 * browser, so the analytics pipeline can distinguish frontend-emitted ad
 * events from server-side equivalents.
 */
export const PICOX_TRACKING_ORIGIN = 'frontend';
export const PICOX_SOURCE = 'listings-web';

// Browser/device context (user_agent, locale, timezone, viewport, landing URL +
// utm/click attribution, etc.) is captured automatically by the PicoX SDK
// (`@eventbrite/picox-web`'s context provider) and the BSP `createAnalytics`
// `additionalContext`. We deliberately only emit ad-domain fields here.

/**
 * Canonical PicoX event names emitted by the ads tracking pipeline.
 * `PromotedEventImpression` / `PromotedEventClick` fire per promoted card and
 * power the spend pipeline (paired with `ad_bid`). `RelatedEventsRecommended`
 * fires once when a related-events list renders and carries parallel arrays
 * for every shown event (promoted + organic). `EventPageOpened` fires when an
 * attendee navigates from a recommendation surface into an event page.
 *
 * Ported from eb-ui `packages/attendee/ads/src/tracking/picoxInteractionsMapper.ts`
 * (PR eventbrite/eb-ui#36290). Names must stay byte-identical across both
 * frontends so the spend pipeline can attribute promoted impressions / clicks
 * uniformly regardless of which app served the page.
 */
export const PicoXAdsEventNames = {
  PromotedEventImpression: 'ads_promoted_eb_event_impression',
  PromotedEventClick: 'ads_promoted_eb_event_click',
  EventPageOpened: 'eb_event_page_opened',
  RelatedEventsRecommended: 'related_eb_events_recommended',
} as const;

/** Scalar primitives PicoX accepts inside a payload field. */
type PicoXPrimitive = string | number | boolean | null;

/**
 * Any value a PicoX payload field can hold: a primitive or an array of
 * primitives. The tracker rejects nested objects, so payloads stay flat
 * and easy to query in the downstream warehouse.
 */
export type PicoXPayloadValue = PicoXPrimitive | PicoXPrimitive[];

/**
 * Whether a recommended event came from the ad auction (`ads`) or organic
 * ranking (`organic`). Reported so analytics can split spend-bearing
 * impressions from free recommendations.
 */
type PicoXRecommendationSource = 'ads' | 'organic';

/**
 * Wire shape of `ads_promoted_eb_event_impression` and `_click`. Carries
 * the impressed slot's identity (`eb_event_id` / `parent_eb_event_id`,
 * both private IDs), auction metadata (`ad_id`, `campaign_id`, `ad_bid`,
 * `placement_id`), surface coordinates (`rank`, `page`,
 * `product_location`), and a few denormalized event traits
 * (`is_online`, `is_free`, `has_urgency_signals`) the spend pipeline
 * indexes on for reporting. Slate context (the parallel `recommended_*`
 * lists of every co-shown event) lives on the separate
 * `related_eb_events_recommended` event per the PicoX Tracking spec.
 */
export type PicoXPromotedEventPayload = {
  tracking_origin: typeof PICOX_TRACKING_ORIGIN;
  product_location: string;
  sub_product_location: string | null;
  eb_event_source: PicoXRecommendationSource;
  ad_id: string | null;
  campaign_id: string | number | null;
  eb_event_id: string;
  parent_eb_event_id: string | null;
  ad_bid: number | null;
  placement_id: string | null;
  rank: number;
  absolute_rank: number | null;
  page: number;
  position: string | null;
  action: EventInteractions;
  is_online: boolean;
  is_free: boolean;
  has_urgency_signals: boolean;
  venue_id: string | null;
};

/**
 * Minimal shape of a recommended event the PicoX builders consume. Accepts
 * both `promotedListingMetadata` (camelCase, app-internal) and
 * `promoted_listing_metadata` (snake_case, the raw Ads Retrieval / BFF shape)
 * so callers can pass through whichever form the surface holds. `listings-webapp`
 * uses camelCase exclusively, but the snake-case fallback keeps the file
 * copy-paste compatible with eb-ui so future fixes flow in both directions.
 */
export type PicoXRecommendedEvent = {
  id?: string | number | null;
  isPromoted?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  promotedListingMetadata?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  promoted_listing_metadata?: any;
};

/**
 * Wire shape of `related_eb_events_recommended`, fired once per render with
 * parallel arrays describing every slot the attendee saw. Promoted slots
 * populate `recommended_eb_event_ad_id` / `_campaign_id`; organic slots
 * emit empty strings in the same positions so positional alignment across
 * the arrays is preserved when the pipeline reconstructs the slate.
 * `eb_event_id` identifies the source event whose page rendered the slate
 * (per the PicoX Tracking spec's `event_id` property). `parent_eb_event_id`
 * mirrors what the legacy `log_engagement` endpoint already emits as
 * `retrieval_context.related_event_id` on related-events surfaces — the
 * surface event id itself — so per-card and slate events stay aligned with
 * the existing engagement payload.
 */
export type PicoXRelatedEventsRecommendedPayload = {
  tracking_origin: typeof PICOX_TRACKING_ORIGIN;
  product_location: string;
  eb_event_id: string | null;
  recommended_eb_event_ids: string[];
  recommended_eb_event_source: PicoXRecommendationSource[];
  recommended_eb_event_ad_id: string[];
  recommended_eb_event_campaign_id: string[];
  parent_eb_event_id: string | null;
};

/**
 * Wire shape of `eb_event_page_opened`, fired when a recommendation card
 * is clicked. Carries enough context (`product_location`,
 * `eb_event_source`, `parent_eb_event_id`) for attribution to tie the
 * destination event page back to the recommendation surface and parent
 * event that drove the navigation.
 */
export type PicoXEventPageOpenedPayload = {
  tracking_origin: typeof PICOX_TRACKING_ORIGIN;
  product_location: string;
  eb_event_source: string | null;
  ad_id: string | null;
  campaign_id: string | number | null;
  eb_event_id: string;
  parent_eb_event_id: string | null;
};

/**
 * Discriminated event envelope produced by
 * `mapInteractionToPicoXPromotedEvent`: a PicoX event name paired with a
 * generic-shaped payload, ready to hand directly to `usePicoXTrack`
 * without further branching at the call site.
 */
export type PicoXPromotedEvent = {
  name: (typeof PicoXAdsEventNames)[keyof typeof PicoXAdsEventNames];
  payload: Record<string, PicoXPayloadValue>;
};

/**
 * Maps an `EventInteraction` action to the matching PicoX event name.
 * Only impressions and clicks have billable PicoX equivalents today;
 * other actions return `null` so the caller can short-circuit instead of
 * emitting an unattributed event.
 */
const getPicoXEventName = (action: EventInteractions): PicoXPromotedEvent['name'] | null => {
  switch (action) {
    case EventInteractions.Impression:
      return PicoXAdsEventNames.PromotedEventImpression;
    case EventInteractions.Click:
      return PicoXAdsEventNames.PromotedEventClick;
    default:
      return null;
  }
};

/**
 * Pulls promoted-listing metadata off an event regardless of whether the
 * surface stored it in camelCase (app-internal) or snake_case (Ads Retrieval).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPromotedMetadata = (event: any = {}) => {
  return event.promotedListingMetadata || event.promoted_listing_metadata || {};
};

/**
 * Labels each surfaced event as `ads` (auction-served) or `organic` so the
 * downstream pipeline can split spend-bearing impressions from free
 * recommendations when reporting.
 */
const getEventSource = (event: PicoXRecommendedEvent): PicoXRecommendationSource =>
  event.isPromoted ? 'ads' : 'organic';

/**
 * Reads a single promoted-metadata field, accepting both the camelCase
 * (app-internal) and snake_case (Ads Retrieval BFF) variants so callers
 * stay agnostic to which surface produced the event. Coerces non-primitive
 * values to `null` to keep PicoX payloads schema-friendly.
 */
const getPromotedMetadataValue = (
  event: PicoXRecommendedEvent,
  camelKey: string,
  snakeKey: string,
): string | number | null => {
  const promotedMetadata = getPromotedMetadata(event);
  const value = promotedMetadata[camelKey] ?? promotedMetadata[snakeKey];

  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

/**
 * Surfaces the ad ID of a promoted slot, coerced to string because PicoX
 * stringifies all ID fields. Returns `null` for organic events so the
 * caller can omit the field rather than emit a misattributed ID.
 */
const getEventAdId = (event: PicoXRecommendedEvent): string | null => {
  const adId = getPromotedMetadataValue(event, 'adId', 'ad_id');

  return adId == null ? null : String(adId);
};

/**
 * Resolves the placement ID for a promoted impression as a `string`. Reads
 * from `promotedListingMetadata` first (the BFF surface sometimes delivers
 * `placement_id` as a number), and falls back to the place-level
 * `placementId` legacy surfaces attach. PicoX rejects non-string IDs, so
 * any non-nullish value is run through `String(...)` before being emitted.
 */
const getEventPlacementId = (event: PicoXRecommendedEvent, fallback?: string | number | null): string | null => {
  const placementId = getPromotedMetadataValue(event, 'placementId', 'placement_id') ?? fallback;

  return placementId == null || placementId === '' ? null : String(placementId);
};

/**
 * Surfaces the campaign ID an attendee saw, reading from promoted-listing
 * metadata in whichever case the upstream payload used. Used to attribute
 * impressions/clicks back to the auction campaign in the spend pipeline.
 */
export const getEventCampaignId = (event: PicoXRecommendedEvent): string | number | null =>
  getPromotedMetadataValue(event, 'campaignId', 'campaign_id');

/**
 * Resolves the campaign ID for an interaction by checking the event's
 * promoted metadata first and falling back to the place-level context
 * older surfaces attach. This keeps attribution working on legacy
 * call-sites that haven't moved campaign ID onto the event itself.
 */
const getCampaignId = (interaction: EventInteractionWithContext): string | number | null => {
  const placeContext = (interaction.place.context ?? {}) as Record<string, unknown>;
  const campaignFromPlace = placeContext.campaign_id ?? placeContext.campaignId;
  const campaignFromEvent = getEventCampaignId(interaction.event as PicoXRecommendedEvent);

  if (campaignFromEvent != null) {
    return campaignFromEvent;
  }

  if (typeof campaignFromPlace === 'string' || typeof campaignFromPlace === 'number') {
    return campaignFromPlace;
  }

  return null;
};

/**
 * Coerces a raw bid value (upstream APIs frequently deliver bids as
 * strings) into a finite `number` so PicoX receives a clean numeric
 * `ad_bid`. Returns `null` for missing or non-numeric input rather than
 * `NaN`, which would corrupt the downstream spend aggregation.
 */
const normalizeAdBid = (value: string | number | null): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const numericBid = Number(value);

  return Number.isFinite(numericBid) ? numericBid : null;
};

/**
 * Surfaces the bid associated with a promoted impression as `ad_bid` on
 * PicoX payloads — used downstream by the spend pipeline as a proxy for
 * what the auction charged. Prefers the auction's goal-adjusted bid (what
 * Insert returns as `goal_adj_bid` after applying campaign objective
 * multipliers) and falls back to the raw `ad_bid` for legacy payloads where
 * Insert hasn't yet attached the goal-adjusted value.
 */
export const getEventAdBid = (event: PicoXRecommendedEvent): number | null =>
  normalizeAdBid(
    getPromotedMetadataValue(event, 'goalAdjBid', 'goal_adj_bid') ?? getPromotedMetadataValue(event, 'adBid', 'ad_bid'),
  );

/**
 * Resolves the private ID of the event page that drove a recommendation,
 * checking every key shape the various tracking surfaces have produced
 * (related_event / relatedEvent objects, *_id flat keys, parent_event
 * aliases). Returns `null` when no parent context is present so PicoX can
 * omit the field rather than report a misattributed parent.
 */
const getParentEventId = (interaction: EventInteractionWithContext): string | null => {
  const placeContext = (interaction.place.context ?? {}) as Record<string, unknown>;
  const relatedEventCamel = placeContext.relatedEvent as { id?: string | number } | undefined;
  const relatedEventSnake = placeContext.related_event as { id?: string | number } | undefined;

  const candidate =
    relatedEventSnake?.id ??
    relatedEventCamel?.id ??
    (placeContext.related_event_id as string | number | undefined) ??
    (placeContext.relatedEventId as string | number | undefined) ??
    (placeContext.parent_event_id as string | number | undefined) ??
    (placeContext.parentEventId as string | number | undefined);

  return toPrivateEventId(candidate ?? null);
};

/**
 * Builds the payload for `ads_promoted_eb_event_impression` /
 * `ads_promoted_eb_event_click`, fired when a promoted card is viewed or
 * clicked. `eb_event_id` and `parent_eb_event_id` are emitted as private IDs
 * (public ID / 1003) because PicoX standardizes on private IDs, and `ad_bid`
 * carries the auction goal-adjusted bid surfaced by Insert so the spend
 * pipeline can attribute cost to each impression/click. `ad_id` and
 * `placement_id` are run through string coercers because the BFF can
 * deliver them as numbers and PicoX validates these as `String`. Slate
 * context (the parallel `recommended_*` lists) is intentionally absent
 * here — per the PicoX Tracking spec it lives on the once-per-render
 * `related_eb_events_recommended` event, not on per-card impressions.
 */
export const buildPicoXPromotedEventPayload = (interaction: EventInteractionWithContext): PicoXPromotedEventPayload => {
  const event = interaction.event as PicoXRecommendedEvent & {
    isOnline?: boolean;
    isOnlineEvent?: boolean;
    isFree?: boolean;
  };
  const campaignId = getCampaignId(interaction);
  const urgencySignals = interaction.displayContext?.urgencySignals || [];
  const source = getEventSource(event);
  const privateEventId = toPrivateEventId(interaction.id) || interaction.id;

  return {
    tracking_origin: PICOX_TRACKING_ORIGIN,
    product_location: interaction.place.name,
    sub_product_location: interaction.place.subInterface?.name || null,
    eb_event_source: source,
    ad_id: getEventAdId(event) ?? (interaction.adId ? String(interaction.adId) : null),
    campaign_id: campaignId,
    eb_event_id: privateEventId,
    parent_eb_event_id: getParentEventId(interaction),
    ad_bid: getEventAdBid(event),
    placement_id: getEventPlacementId(event, interaction.place.placementId),
    rank: interaction.place.position,
    absolute_rank: interaction.place.absoluteRank ?? null,
    page: interaction.place.page,
    position: interaction.place.subInterface?.position || null,
    action: interaction.action,
    is_online: Boolean(event.isOnline ?? event.isOnlineEvent),
    is_free: Boolean(event.isFree),
    has_urgency_signals: urgencySignals.length > 0,
    venue_id: interaction.venueId || null,
  };
};

/**
 * Builds the payload for `related_eb_events_recommended`, fired once when a
 * recommendation surface (e.g. the related-events carousel) renders its full
 * slate of events. Promoted and organic events share parallel arrays so the
 * downstream pipeline can reconstruct the rendered order; promoted slots
 * carry their `ad_id` / `campaign_id`, organic slots emit empty strings in
 * the same positions. `eb_event_id` is the private ID of the source event
 * whose page rendered the recommendation slate (Notion calls this
 * `event_id`); `parent_eb_event_id` mirrors what the legacy
 * `/api/v3/log_engagement/` endpoint already emits as
 * `retrieval_context.related_event_id` for related-events surfaces — the
 * surface event id itself — so the per-card and slate events stay aligned
 * with the existing engagement payload.
 */
export const buildPicoXRelatedEventsRecommendedPayload = ({
  events,
  productLocation,
  eventId,
  parentEventId,
}: {
  events: PicoXRecommendedEvent[];
  productLocation: string;
  eventId?: string | number | null;
  parentEventId?: string | number | null;
}): PicoXRelatedEventsRecommendedPayload => ({
  tracking_origin: PICOX_TRACKING_ORIGIN,
  product_location: productLocation,
  eb_event_id: toPrivateEventId(eventId),
  recommended_eb_event_ids: events.map((event) => toPrivateEventId(event.id) || ''),
  recommended_eb_event_source: events.map(getEventSource),
  recommended_eb_event_ad_id: events.map((event) => (event.isPromoted ? getEventAdId(event) || '' : '')),
  recommended_eb_event_campaign_id: events.map((event) => {
    const campaignId = event.isPromoted ? getEventCampaignId(event) : null;
    return campaignId == null ? '' : String(campaignId);
  }),
  parent_eb_event_id: toPrivateEventId(parentEventId),
});

/**
 * Builds the payload for `eb_event_page_opened`, fired when an attendee
 * clicks a recommendation card and navigates into an event page. Captures
 * the originating surface (`product_location` / `eb_event_source`) and the
 * parent event whose recommendations drove the click, so attribution can
 * tie page opens back to the recommendation that produced them.
 */
export const buildPicoXEventPageOpenedPayload = ({
  event,
  productLocation,
  eventSource,
  parentEventId,
}: {
  event: PicoXRecommendedEvent;
  productLocation: string;
  eventSource?: string | null;
  parentEventId?: string | number | null;
}): PicoXEventPageOpenedPayload => ({
  tracking_origin: PICOX_TRACKING_ORIGIN,
  product_location: productLocation,
  eb_event_source: eventSource || null,
  ad_id: event.isPromoted ? getEventAdId(event) : null,
  campaign_id: event.isPromoted ? getEventCampaignId(event) : null,
  eb_event_id: toPrivateEventId(event.id) || String(event.id ?? ''),
  parent_eb_event_id: toPrivateEventId(parentEventId),
});

export const PicoXFollowVenueEventNames = {
  Shown: 'follow_venue_shown',
  Tapped: 'follow_venue_tapped',
  Untapped: 'unfollow_venue_tapped',
} as const;

export type PicoXFollowVenuePayload = {
  tracking_origin: typeof PICOX_TRACKING_ORIGIN;
  source: typeof PICOX_SOURCE;
  eb_event_id: string | null;
  venue_id: string | null;
  venue_name: string | null;
  user_id: string | null;
  organizer_id: string | null;
};

export const PicoXFollowArtistEventNames = {
  Shown: 'follow_artist_shown',
  Tapped: 'follow_artist_tapped',
  Untapped: 'unfollow_artist_tapped',
} as const;

export type PicoXFollowArtistPayload = {
  tracking_origin: typeof PICOX_TRACKING_ORIGIN;
  source: typeof PICOX_SOURCE;
  eb_event_id: string | null;
  artist_id: string | null;
  artist_name: string | null;
  user_id: string | null;
  organizer_id: string | null;
};

/**
 * Bridges the generic `EventInteraction` event-bus signal into a concrete
 * PicoX event for the spend pipeline. Returns `null` for organic events
 * (only promoted impressions/clicks are billable) and for action types we
 * don't track yet, so the call site can short-circuit without branching.
 */
export const mapInteractionToPicoXPromotedEvent = (
  interaction: EventInteractionWithContext,
): PicoXPromotedEvent | null => {
  const event = interaction.event as PicoXRecommendedEvent;
  if (!event?.isPromoted) {
    return null;
  }

  const name = getPicoXEventName(interaction.action);

  if (!name) {
    return null;
  }

  return {
    name,
    payload: buildPicoXPromotedEventPayload(interaction) as Record<string, PicoXPayloadValue>,
  };
};
